/**
 * ContentBlock — Content 块渲染（简化自 ContentBlockRenderer）。
 *
 * 三种状态：
 * 1. 静态 content → 直接 BlockList(parsedBlocks)
 * 2. 动态 content 未执行 → 占位符 + 运行按钮
 * 3. 动态 content 已执行 → ScopeProvider + BlockList(parsedBlocks)
 */

import type {
    ContentDefinition,
    ContentOutput,
    DocumentBlock,
    ForDefinition,
    ForOutput,
    ImportCachedOutput,
    PluginDefinition,
    PluginOutput,
    RenderMode,
} from "@magic-md/core";
import {
    buildDefaultedTemplateInputs,
    getBlockingTemplateParams,
    extractTemplateParams,
    parseMagicMd,
    substituteTemplateInputRefs,
} from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import {
    AlertTriangle,
    FileText,
    Loader2,
    Maximize2,
    Minimize2,
    Play,
    RefreshCw,
    StretchHorizontal,
} from "lucide-react";
import {
    type CSSProperties,
    type MouseEvent,
    type ReactNode,
    createElement,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useJustFinishedFlash } from "../../../hooks/useJustFinishedFlash";
import { contentNeedsExecution } from "../../../lib/document-render-logic";
import { getImportCachedSymbolOutput } from "../../../lib/import-cache-output";
import {
    getMissingRequiredTemplateInputsForUse,
    isUseBindingBlockedAsTemplate,
} from "../../../lib/template-unit-state";
import { materializePluginUseDefinition } from "../../../lib/use-materialization";
import {
    PREVIEW_HORIZONTAL_PADDING,
    PREVIEW_WIDTH_PRESETS,
    type PreviewWidthControl,
    clampPreviewViewportWidth,
    resolvePreviewViewportWidth,
} from "../../../lib/preview-width";
import { cn } from "../../../lib/utils";
export { contentNeedsExecution } from "../../../lib/document-render-logic";
import type { ContentExecutionEntry } from "../../../store/plugin-execution-store";
import {
    getThemeAccentStyle,
    getThemeAccentValue,
} from "../../../theme/runtime/theme-accents";
import { resolveImportedSymbolNamespace } from "../../../preview-logic";
import { AnnotationBlockFooterControl } from "../../annotations/AnnotationBlockTarget";
import { buildAnnotationOutputHash } from "../../annotations/hash";
import {
    findContentDefinitionInScope,
    findOutputInScope,
    ScopeProvider,
    useScopeContext,
} from "../../ScopeContext";
import { useDocContext, useDocOutputs } from "../contexts";
import { useImportExecution } from "../contexts/ExecutionContext";
import { BlockList } from "./BlockList";
import {
    CollapsedUnitRenderHeader,
    type CollapsedUnitRenderStatus,
} from "./CollapsedUnitRenderHeader";
import { readRenderExpanded } from "./render-attrs";
import { RenderUnitFooterDisclosure } from "./RenderUnitFooterDisclosure";
import {
    type UnitFooterCopyAction,
    stringifyUnitFooterJson,
    UnitFooterCopyButton,
} from "./UnitFooterCopyButton";
import { UnitDependencyFooterButton } from "./UnitDependencyFooterButton";
import { TemplateRenderPlaceholder } from "./TemplateRenderPlaceholder";
import { RenderUnitBadge, type RenderUnitBadgeProps } from "./RenderUnitBadge";
import { TemplateUsageWarning } from "./TemplateUsageWarning";

const WORKSPACE_FULLSCREEN_SELECTOR = "[data-magic-editor-preview-workspace='true']";

const renderInnerBlockItem = ({ children }: { children: ReactNode }) => (
    <div className="magic-render-inner-block-item">{children}</div>
);

type WorkspaceFullscreenRect = {
    top: number;
    left: number;
    width: number;
    height: number;
};

function findWorkspaceFullscreenHost(element: HTMLElement | null): HTMLElement | null {
    return element?.closest<HTMLElement>(WORKSPACE_FULLSCREEN_SELECTOR) ?? null;
}

function readWorkspaceFullscreenRect(host: HTMLElement | null): WorkspaceFullscreenRect | null {
    if (typeof window === "undefined") return null;

    if (host) {
        const rect = host.getBoundingClientRect();
        return {
            top: Math.round(rect.top),
            left: Math.round(rect.left),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
        };
    }

    const visualViewport = window.visualViewport;
    return {
        top: Math.round(visualViewport?.offsetTop ?? 0),
        left: Math.round(visualViewport?.offsetLeft ?? 0),
        width: Math.round(visualViewport?.width ?? window.innerWidth),
        height: Math.round(visualViewport?.height ?? window.innerHeight),
    };
}

function readFullscreenAvailableWidth(rect: WorkspaceFullscreenRect | null): number | null {
    if (rect) return Math.max(1, rect.width - PREVIEW_HORIZONTAL_PADDING);
    if (typeof window === "undefined") return null;
    return Math.max(1, window.innerWidth - PREVIEW_HORIZONTAL_PADDING);
}

function formatPreviewWidthLabel(
    t: ReturnType<typeof useT>,
    control: PreviewWidthControl,
    customWidth: number | null,
): string {
    if (control === "custom") {
        return customWidth
            ? t("previewWidth.customWithWidth", {
                width: Math.round(customWidth),
            })
            : t("previewWidth.custom");
    }
    return t(`previewWidth.${control}`);
}

function isForOutput(value: unknown): value is ForOutput {
    return typeof value === "object" && value !== null
        && Array.isArray((value as { iterations?: unknown }).iterations);
}

function isImportCachedOutput(value: unknown): value is ImportCachedOutput {
    return typeof value === "object" && value !== null
        && Array.isArray((value as { symbols?: unknown }).symbols);
}

function isReservedOutputKey(key: string): boolean {
    return key.startsWith("__");
}

export function contentOutputToScopeUnitOutputs(
    output: ContentOutput,
): Record<string, PluginOutput> {
    return Object.fromEntries(
        Object.entries(output)
            .filter(([key, value]) => !isReservedOutputKey(key) && value !== undefined)
            .map(([key, value]) => [key, value as PluginOutput]),
    );
}

function mergeImportOutputIntoScopedUnits(
    namespace: string,
    output: ImportCachedOutput,
    definitions: Map<string, PluginDefinition>,
    contentDefinitions: Map<string, ContentDefinition>,
    forDefinitions: Map<string, ForDefinition>,
    allDefinitions: Map<string, PluginDefinition>,
    allContentDefinitions: Map<string, ContentDefinition>,
    unitOutputs: Record<string, PluginOutput>,
): void {
    const symbols = [...(output.symbols ?? []), ...(output.instanceSymbols ?? [])];
    for (const sym of symbols) {
        const cachedOutput = getImportCachedSymbolOutput(namespace, output, sym);
        if (sym.kind === "node" && sym.pluginName && !definitions.has(sym.qualifiedId)) {
            const definition: PluginDefinition = {
                id: sym.qualifiedId,
                pluginName: sym.pluginName,
                config: sym.config ?? {},
                template: sym.template ?? false,
                params: sym.params,
                ...(cachedOutput && !isForOutput(cachedOutput)
                    ? { output: cachedOutput as PluginOutput }
                    : {}),
            };
            definitions.set(sym.qualifiedId, definition);
            allDefinitions.set(sym.qualifiedId, definition);
            if (definition.output) unitOutputs[sym.qualifiedId] = definition.output;
            continue;
        }

        if (sym.kind === "content" && sym.body != null && !contentDefinitions.has(sym.qualifiedId)) {
            const contentOutput = cachedOutput && !isForOutput(cachedOutput)
                ? cachedOutput as ContentOutput
                : undefined;
            const contentDefinition: ContentDefinition = {
                id: sym.qualifiedId,
                kind: "content",
                template: sym.template ?? false,
                body: sym.body,
                params: sym.params,
                range: { start: 0, end: 0 },
                ...(contentOutput ? { output: contentOutput } : {}),
            };
            contentDefinitions.set(sym.qualifiedId, contentDefinition);
            allContentDefinitions.set(sym.qualifiedId, contentDefinition);
            continue;
        }

        if (sym.kind === "for" && sym.forDefinition && !forDefinitions.has(sym.qualifiedId)) {
            const forOutput = isForOutput(cachedOutput) ? cachedOutput : undefined;
            forDefinitions.set(sym.qualifiedId, {
                ...sym.forDefinition,
                id: sym.qualifiedId,
                ...(forOutput ? { output: forOutput } : {}),
            });
        }
    }
}

export function buildScopedUnits(
    content: ContentDefinition,
    availableDefinitions: Map<string, PluginDefinition> | undefined,
    availableContentDefinitions: Map<string, ContentDefinition> | undefined,
    availableOutputs: Map<string, PluginOutput> | undefined,
    effectiveOutput: ContentOutput | undefined,
) {
    const parsed = parseMagicMd(content.body);
    const definitions = new Map(parsed.definitions);
    const contentDefinitions = new Map(parsed.contentDefinitions ?? new Map<string, ContentDefinition>());
    const forDefinitions = new Map(parsed.forDefinitions ?? new Map<string, ForDefinition>());
    const allDefinitions = new Map(availableDefinitions ?? new Map<string, PluginDefinition>());
    const allContentDefinitions = new Map(availableContentDefinitions ?? new Map<string, ContentDefinition>());
    const unitOutputs: Record<string, PluginOutput> = {};

    for (const [id, def] of definitions) allDefinitions.set(id, def);
    for (const [id, def] of contentDefinitions) allContentDefinitions.set(id, def);

    for (const [id, def] of definitions) {
        const outputValue =
            availableOutputs?.get(id) ??
            availableDefinitions?.get(id)?.output ??
            effectiveOutput?.[id];
        if (outputValue) def.output = outputValue as PluginOutput;
    }
    for (const [id, def] of contentDefinitions) {
        const outputValue = availableContentDefinitions?.get(id)?.output ?? effectiveOutput?.[id];
        if (outputValue && !isForOutput(outputValue)) def.output = outputValue as ContentOutput;
    }
    for (const [id, def] of forDefinitions) {
        const outputValue = effectiveOutput?.[id];
        if (isForOutput(outputValue)) def.output = outputValue;
    }

    for (const use of parsed.uses ?? []) {
        if (definitions.has(use.id) || contentDefinitions.has(use.id)) continue;

        const materializedDefinition = materializePluginUseDefinition(
            use,
            allDefinitions,
            parsed.uses ?? [],
        );
        if (materializedDefinition) {
            const outputValue =
                availableOutputs?.get(use.id) ??
                availableDefinitions?.get(use.id)?.output ??
                effectiveOutput?.[use.id];
            const withOutput = outputValue
                ? { ...materializedDefinition, output: outputValue as PluginOutput }
                : materializedDefinition;
            definitions.set(use.id, withOutput);
            allDefinitions.set(use.id, withOutput);
            continue;
        }

        const sourceContent = allContentDefinitions.get(use.source);
        if (sourceContent) {
            const outputValue = availableContentDefinitions?.get(use.id)?.output ?? effectiveOutput?.[use.id];
            const body = sourceContent.template
                ? substituteTemplateInputRefs(
                    sourceContent.body,
                    buildDefaultedTemplateInputs(use.with, sourceContent.inputSchema),
                )
                : sourceContent.body;
            const params = extractTemplateParams(body);
            const blockingParams = getBlockingTemplateParams(
                sourceContent.inputSchema,
                sourceContent.params,
                params,
            );
            const remainsTemplate = use.template === true || blockingParams.length > 0;
            const materializedContent: ContentDefinition = {
                ...sourceContent,
                id: use.id,
                template: remainsTemplate,
                params: params.length > 0 ? params : undefined,
                body,
                ...(use.outputMode ? { outputMode: use.outputMode } : {}),
                ...(use.externalRef ? { externalRef: use.externalRef } : {}),
                ...(outputValue ? { output: outputValue as ContentOutput } : {}),
            };
            contentDefinitions.set(use.id, materializedContent);
            allContentDefinitions.set(use.id, materializedContent);
        }
    }

    for (const imp of parsed.imports ?? []) {
        const rawOutput = effectiveOutput?.[imp.namespace] ?? imp.output;
        if (!isImportCachedOutput(rawOutput)) continue;
        mergeImportOutputIntoScopedUnits(
            imp.namespace,
            rawOutput,
            definitions,
            contentDefinitions,
            forDefinitions,
            allDefinitions,
            allContentDefinitions,
            unitOutputs,
        );
    }

    for (const [id, def] of definitions) {
        const outputValue = def.output ?? effectiveOutput?.[id];
        if (outputValue && !isForOutput(outputValue)) {
            unitOutputs[id] = outputValue as PluginOutput;
        }
    }

    return { blocks: parsed.blocks, definitions, contentDefinitions, forDefinitions, unitOutputs };
}

function ContentMemberRenderer({
    memberPath,
    attrs,
    mode,
    defaultExpanded,
}: {
    memberPath: string[];
    attrs?: Record<string, string>;
    mode: RenderMode;
    defaultExpanded?: boolean;
}) {
    const scope = useScopeContext();
    const [head, ...tail] = memberPath;
    if (!head) return null;

    if (tail.length > 0) {
        const nestedContent = findContentDefinitionInScope(scope, head);
        if (!nestedContent) {
            return (
                <span className="text-xs text-muted-foreground">
                    {memberPath.join("/")}
                </span>
            );
        }
        return (
            <ContentBlock
                content={nestedContent}
                definitionId={head}
                execution={undefined}
                mode={mode}
                attrs={attrs}
                defaultExpanded={defaultExpanded}
                instanceKind="scoped-content"
                renderMemberPath={tail}
            />
        );
    }

    const block: DocumentBlock = mode === "inline"
        ? {
            type: "inline-render",
            definitionId: head,
            attrs: attrs ?? {},
        }
        : {
            type: "block-render",
            definitionId: head,
            attrs: attrs ?? {},
        };
    return <BlockList blocks={[block]} />;
}

function getContentRenderMeta(
    t: ReturnType<typeof useT>,
    instanceKind: "content" | "use-content" | "scoped-content",
): {
    label: string;
    technicalLabel: string;
    badge: Pick<RenderUnitBadgeProps, "main" | "sub" | "sup" | "template">;
} {
    if (instanceKind === "use-content") {
        return {
            label: t("render.content.instance"),
            technicalLabel: "use content",
            badge: { main: "U", sub: "C" },
        };
    }
    if (instanceKind === "scoped-content") {
        return {
            label: t("render.content.scoped"),
            technicalLabel: "scoped content",
            badge: { main: "C" },
        };
    }
    return {
        label: "Content",
        technicalLabel: "content",
        badge: { main: "C" },
    };
}

/* ── ContentBlock ── */

export const ContentBlock = memo(function ContentBlock({
    content,
    definitionId,
    execution,
    mode,
    attrs,
    defaultExpanded = true,
    onRun,
    instanceKind = "content",
    renderMemberPath,
}: {
    content: ContentDefinition;
    definitionId: string;
    execution: ContentExecutionEntry | undefined;
    mode: RenderMode;
    attrs?: Record<string, string>;
    defaultExpanded?: boolean;
    onRun?: (definitionId: string) => void;
    instanceKind?: "content" | "use-content" | "scoped-content";
    renderMemberPath?: string[];
}) {
    const t = useT("editor");
    const doc = useDocContext();
    const outputs = useDocOutputs();
    const scope = useScopeContext();
    const handleRun = useCallback(() => { onRun?.(definitionId); }, [definitionId, onRun]);
    const importNamespace = useMemo(
        () =>
            resolveImportedSymbolNamespace(
                definitionId,
                doc.importBindings,
                doc.localExecutionUnitIds,
        ),
        [definitionId, doc.importBindings, doc.localExecutionUnitIds],
    );
    const importExecution = useImportExecution(importNamespace);
    const isImportRunning = importExecution?.status === "running";
    const canRunImport = !!importNamespace && !!doc.onRunImport;
    const handleRunImport = useCallback((event?: MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        if (!canRunImport || !importNamespace || isImportRunning) return;
        doc.onRunImport?.(importNamespace);
    }, [canRunImport, doc.onRunImport, importNamespace, isImportRunning]);
    const initialExpanded = readRenderExpanded(attrs, defaultExpanded);
    const [expanded, setExpanded] = useState(initialExpanded);
    const renderContainerRef = useRef<HTMLDivElement | null>(null);
    const renderBodyRef = useRef<HTMLDivElement | null>(null);
    const fullscreenResizeRef = useRef<{
        side: "left" | "right";
        startX: number;
        startWidth: number;
    } | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [workspaceFullscreenRect, setWorkspaceFullscreenRect] =
        useState<WorkspaceFullscreenRect | null>(null);
    const [fullscreenWidthPreset, setFullscreenWidthPreset] =
        useState<PreviewWidthControl>("fill");
    const [customFullscreenWidth, setCustomFullscreenWidth] = useState<number | null>(null);
    const [isFullscreenWidthMenuOpen, setIsFullscreenWidthMenuOpen] = useState(false);
    const [isFullscreenWidthDragging, setIsFullscreenWidthDragging] = useState(false);
    const deferredOutputRequestKeyRef = useRef<string | null>(null);
    const [isLoadingDeferredOutput, setIsLoadingDeferredOutput] = useState(false);
    const [deferredOutputError, setDeferredOutputError] = useState<string | null>(null);

    useEffect(() => {
        setExpanded(initialExpanded);
    }, [definitionId, initialExpanded]);

    useEffect(() => {
        if (!isFullscreen || typeof window === "undefined") return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            if (isFullscreenWidthMenuOpen) {
                setIsFullscreenWidthMenuOpen(false);
                return;
            }
            setIsFullscreen(false);
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isFullscreen, isFullscreenWidthMenuOpen]);

    useEffect(() => {
        if (!isFullscreen) {
            setWorkspaceFullscreenRect(null);
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreenWidthDragging(false);
            return;
        }

        const workspaceHost = findWorkspaceFullscreenHost(renderContainerRef.current);

        const updateRect = () => {
            const next = readWorkspaceFullscreenRect(workspaceHost);
            if (!next) return;
            setWorkspaceFullscreenRect((current) =>
                current?.top === next.top &&
                    current?.left === next.left &&
                    current?.width === next.width &&
                    current?.height === next.height
                    ? current
                    : next,
            );
        };

        updateRect();
        window.addEventListener("resize", updateRect);
        window.addEventListener("scroll", updateRect, true);

        if (typeof ResizeObserver === "undefined" || !workspaceHost) {
            return () => {
                window.removeEventListener("resize", updateRect);
                window.removeEventListener("scroll", updateRect, true);
            };
        }

        const observer = new ResizeObserver(updateRect);
        observer.observe(workspaceHost);
        return () => {
            window.removeEventListener("resize", updateRect);
            window.removeEventListener("scroll", updateRect, true);
            observer.disconnect();
        };
    }, [isFullscreen]);

    const fullscreenAvailableWidth = isFullscreen
        ? readFullscreenAvailableWidth(workspaceFullscreenRect)
        : null;
    const resolvedFullscreenViewportWidth = isFullscreen
        ? resolvePreviewViewportWidth(
            fullscreenWidthPreset,
            customFullscreenWidth,
            fullscreenAvailableWidth,
        )
        : "fill";
    const currentFullscreenWidthLabel = formatPreviewWidthLabel(
        t,
        fullscreenWidthPreset,
        customFullscreenWidth,
    );

    const handleToggleFullscreen = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (isFullscreen) {
                setIsFullscreen(false);
                return;
            }

            const workspaceHost = findWorkspaceFullscreenHost(renderContainerRef.current);
            setWorkspaceFullscreenRect(readWorkspaceFullscreenRect(workspaceHost));
            setFullscreenWidthPreset("fill");
            setCustomFullscreenWidth(null);
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreen(true);
        },
        [isFullscreen],
    );

    const handleFullscreenWidthPresetSelect = useCallback(
        (preset: Exclude<PreviewWidthControl, "custom">) => {
            setFullscreenWidthPreset(preset);
            if (preset === "fill") setCustomFullscreenWidth(null);
            setIsFullscreenWidthMenuOpen(false);
        },
        [],
    );

    const startFullscreenWidthResize = useCallback(
        (side: "left" | "right", event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const rect = renderBodyRef.current?.getBoundingClientRect();
            const currentWidth =
                rect?.width ??
                (resolvedFullscreenViewportWidth === "fill"
                    ? fullscreenAvailableWidth
                    : resolvedFullscreenViewportWidth);
            if (!currentWidth) return;
            fullscreenResizeRef.current = {
                side,
                startX: event.clientX,
                startWidth: currentWidth,
            };
            setFullscreenWidthPreset("custom");
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreenWidthDragging(true);
        },
        [fullscreenAvailableWidth, resolvedFullscreenViewportWidth],
    );

    const resetFullscreenWidth = useCallback(() => {
        setFullscreenWidthPreset("fill");
        setCustomFullscreenWidth(null);
        setIsFullscreenWidthMenuOpen(false);
    }, []);

    useEffect(() => {
        if (!isFullscreenWidthDragging || typeof window === "undefined") return;

        const onMove = (event: globalThis.MouseEvent) => {
            const state = fullscreenResizeRef.current;
            const availableWidth = readFullscreenAvailableWidth(workspaceFullscreenRect);
            if (!state || !availableWidth) return;
            const delta = event.clientX - state.startX;
            const nextWidth =
                state.side === "right"
                    ? state.startWidth + delta
                    : state.startWidth - delta;
            setFullscreenWidthPreset("custom");
            setCustomFullscreenWidth(clampPreviewViewportWidth(nextWidth, availableWidth));
        };

        const onUp = () => {
            fullscreenResizeRef.current = null;
            setIsFullscreenWidthDragging(false);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isFullscreenWidthDragging, workspaceFullscreenRect]);

    const renderBodyStyle =
        isFullscreen
            ? ({
                width: resolvedFullscreenViewportWidth === "fill"
                    ? "100%"
                    : `${resolvedFullscreenViewportWidth}px`,
                maxWidth: "100%",
            } as CSSProperties)
            : undefined;

    const fullscreenContainerStyle =
        isFullscreen && workspaceFullscreenRect
            ? ({
                top: workspaceFullscreenRect.top,
                left: workspaceFullscreenRect.left,
                width: workspaceFullscreenRect.width,
                height: workspaceFullscreenRect.height,
            } as CSSProperties)
            : undefined;

    const needsExec = contentNeedsExecution(content);
    const useBinding = useMemo(
        () => doc.useBindings?.find((binding) => binding.id === definitionId),
        [definitionId, doc.useBindings],
    );
    const missingRequiredTemplateInputs = useMemo(
        () =>
            useBinding
                ? getMissingRequiredTemplateInputsForUse(useBinding, {
                    definitions: doc.definitions,
                    contentDefinitions: doc.contentDefinitions,
                    uses: doc.useBindings,
                })
                : [],
        [doc.contentDefinitions, doc.definitions, doc.useBindings, useBinding],
    );
    const isTemplateContent = useBinding
        ? isUseBindingBlockedAsTemplate(useBinding, missingRequiredTemplateInputs)
        : !!content.template;
    const scopedContentOutput = findOutputInScope(scope, definitionId) as ContentOutput | undefined;
    const effectiveExecutionStatus = scopedContentOutput ? "success" : execution?.status;
    const effectiveExecutionDuration = scopedContentOutput ? null : execution?.duration;
    const effectiveExecutionError = scopedContentOutput ? undefined : execution?.error;
    const contentOutput =
        scopedContentOutput ??
        (execution?.status === "success" && execution.output
            ? execution.output
            : undefined) ??
        content.output;
    const deferredExternalRef = content.externalRef ?? useBinding?.externalRef;
    const deferredOutputRequestTarget = deferredExternalRef || importNamespace
        ? definitionId
        : null;

    useEffect(() => {
        if (!contentOutput) return;
        setIsLoadingDeferredOutput(false);
        setDeferredOutputError(null);
    }, [contentOutput]);

    useEffect(() => {
        if (contentOutput || !deferredOutputRequestTarget || !doc.onLoadOutput) {
            return;
        }

        const requestKey = deferredExternalRef
            ? `${definitionId}\u0000${deferredExternalRef.protocol}\u0000${deferredExternalRef.locator}`
            : `${definitionId}\u0000import-symbol\u0000${importNamespace ?? ""}`;
        if (deferredOutputRequestKeyRef.current === requestKey) return;
        deferredOutputRequestKeyRef.current = requestKey;

        let cancelled = false;
        setIsLoadingDeferredOutput(true);
        setDeferredOutputError(null);

        doc.onLoadOutput(deferredOutputRequestTarget)
            .catch((err) => {
                if (cancelled) return;
                setDeferredOutputError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if (!cancelled) setIsLoadingDeferredOutput(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        contentOutput,
        deferredExternalRef,
        deferredOutputRequestTarget,
        definitionId,
        doc.onLoadOutput,
        importNamespace,
    ]);
    const scopedUnits = useMemo(() => {
        try {
            return buildScopedUnits(
                content,
                doc.definitions,
                doc.contentDefinitions,
                outputs,
                contentOutput,
            );
        } catch {
            return {
                blocks: [] as ReturnType<typeof parseMagicMd>["blocks"],
                definitions: new Map<string, PluginDefinition>(),
                contentDefinitions: new Map<string, ContentDefinition>(),
                forDefinitions: new Map<string, ForDefinition>(),
                unitOutputs: {} as Record<string, PluginOutput>,
            };
        }
    }, [content, doc.definitions, doc.contentDefinitions, outputs, contentOutput]);

    const persistedInnerOutputs = useMemo<Record<string, PluginOutput>>(() => {
        const restored: Record<string, PluginOutput> = {};
        for (const [id, def] of scopedUnits.definitions) {
            if (def.output) restored[id] = def.output;
        }
        return restored;
    }, [scopedUnits.definitions]);
    const hasPersistedInnerOutputs = Object.keys(persistedInnerOutputs).length > 0;
    const isMemberRenderWaitingForRootOutput =
        !!renderMemberPath?.length && needsExec && !contentOutput && !hasPersistedInnerOutputs;
    const copyableContentOutput = contentOutput
        ?? (hasPersistedInnerOutputs ? persistedInnerOutputs : undefined);

    const hasScopedUnits =
        scopedUnits.definitions.size > 0 ||
        scopedUnits.contentDefinitions.size > 0 ||
        scopedUnits.forDefinitions.size > 0;

    // Parse body blocks for BlockList
    const bodyBlocks = useMemo(() => {
        try {
            return parseMagicMd(content.body).blocks;
        } catch {
            return [{ type: "markdown" as const, content: content.body }];
        }
    }, [content.body]);
    const copyReferencePath = `${definitionId}.output`;
    const copyReferenceText = `$ref{${copyReferencePath}}`;
    const copyActions = useMemo<UnitFooterCopyAction[]>(() => [
        {
            key: "copy-reference",
            label: t("copy.reference"),
            text: copyReferenceText,
            icon: "reference",
        },
        {
            key: "copy-path",
            label: t("copy.path"),
            text: copyReferencePath,
            icon: "path",
        },
        {
            key: "copy-id",
            label: t("copy.id"),
            text: definitionId,
            icon: "id",
        },
        {
            key: "copy-body-reference",
            label: t("copy.bodyReference"),
            text: `$ref{${definitionId}.body}`,
            icon: "body",
            separator: true,
        },
        {
            key: "copy-body",
            label: t("copy.contentBody"),
            text: content.body,
            icon: "body",
        },
        ...(copyableContentOutput
            ? [{
                key: "copy-output",
                label: t("copy.output"),
                text: stringifyUnitFooterJson(copyableContentOutput),
                icon: "output" as const,
                separator: true,
            }]
            : []),
        ...(doc.documentPath
            ? [{
                key: "copy-document-path",
                label: t("copy.documentPath"),
                text: doc.documentPath,
                icon: "document" as const,
                separator: true,
            }]
            : []),
    ], [
        content.body,
        copyReferencePath,
        copyReferenceText,
        copyableContentOutput,
        definitionId,
        doc.documentPath,
        t,
    ]);
    const annotationContentOutput = useMemo(
        () => copyableContentOutput ?? {
            kind: "content",
            id: definitionId,
            body: content.body,
        },
        [content.body, copyableContentOutput, definitionId],
    );
    const outputHash = useMemo(
        () => buildAnnotationOutputHash({
            output: annotationContentOutput,
            externalRef: deferredExternalRef,
        }),
        [annotationContentOutput, deferredExternalRef],
    );
    const justFinished = useJustFinishedFlash(effectiveExecutionStatus, {
        suppress: effectiveExecutionStatus === "error",
    });
    const missingTemplateParams = useMemo(() => {
        if (isTemplateContent) return [];
        return Array.from(new Set([
            ...(content.params ?? []),
            ...extractTemplateParams(content.body),
        ]));
    }, [content.body, content.params, isTemplateContent]);
    const hasMissingTemplateParams = !useBinding && missingTemplateParams.length > 0;
    const renderCurrentBody = () => (
        renderMemberPath?.length
            ? (
                <ContentMemberRenderer
                    memberPath={renderMemberPath}
                    attrs={attrs}
                    mode={mode}
                    defaultExpanded={defaultExpanded}
                />
            )
            : (
                <BlockList
                    blocks={bodyBlocks}
                    className="magic-render-inner-block-list"
                    blockWrapper={renderInnerBlockItem}
                />
            )
    );

    if (isTemplateContent) {
        return (
            <TemplateRenderPlaceholder
                unitId={definitionId}
                kindLabel="Content"
                technicalLabel={instanceKind}
                params={content.params}
                mode={mode}
                badgeMain="C"
                badgeSub="T"
            />
        );
    }

    if (hasMissingTemplateParams) {
        const meta = getContentRenderMeta(t, instanceKind);
        return (
            <TemplateUsageWarning
                unitId={definitionId}
                kindLabel={meta.label}
                technicalLabel={meta.technicalLabel}
                mode={mode}
                params={missingTemplateParams}
                badge={meta.badge}
                locationLabel="body"
            />
        );
    }

    if (mode === "inline") {
        if (effectiveExecutionStatus === "running" || isLoadingDeferredOutput) {
            return (
                <ContentPlaceholder
                    definitionId={definitionId}
                    status="running"
                    mode={mode}
                    instanceKind={instanceKind}
                />
            );
        }

        if (effectiveExecutionStatus === "error" || deferredOutputError) {
            return (
                <span
                    className="inline-flex items-center gap-1 text-[10px] theme-accent-text"
                    style={getThemeAccentStyle("error")}
                >
                    <span>⚠</span>
                    <span className="opacity-80" title={deferredOutputError ?? undefined}>{definitionId}</span>
                </span>
            );
        }

        if (isMemberRenderWaitingForRootOutput) {
            return (
                <ContentPlaceholder
                    definitionId={definitionId}
                    status="idle"
                    mode={mode}
                    instanceKind={instanceKind}
                    onRun={handleRun}
                />
            );
        }

        if (!needsExec && !hasScopedUnits) {
            return renderCurrentBody();
        }

        if (contentOutput) {
            return (
                <ScopeProvider
                    definitions={scopedUnits.definitions}
                    forDefinitions={scopedUnits.forDefinitions}
                    contentDefinitions={scopedUnits.contentDefinitions}
                    unitOutputs={{
                        ...contentOutputToScopeUnitOutputs(contentOutput),
                        ...scopedUnits.unitOutputs,
                    }}
                >
                    {renderCurrentBody()}
                </ScopeProvider>
            );
        }

        if (hasPersistedInnerOutputs) {
            return (
                <ScopeProvider
                    definitions={scopedUnits.definitions}
                    forDefinitions={scopedUnits.forDefinitions}
                    contentDefinitions={scopedUnits.contentDefinitions}
                    unitOutputs={persistedInnerOutputs}
                >
                    {renderCurrentBody()}
                </ScopeProvider>
            );
        }

        if (hasScopedUnits) {
            return (
                <ScopeProvider
                    definitions={scopedUnits.definitions}
                    forDefinitions={scopedUnits.forDefinitions}
                    contentDefinitions={scopedUnits.contentDefinitions}
                    unitOutputs={scopedUnits.unitOutputs}
                >
                    {renderCurrentBody()}
                </ScopeProvider>
            );
        }

        return (
            <ContentPlaceholder
                definitionId={definitionId}
                status="idle"
                mode={mode}
                instanceKind={instanceKind}
                onRun={handleRun}
            />
        );
    }

    let body: React.ReactNode;

    if (!needsExec && !hasScopedUnits) {
        body = renderCurrentBody();
    } else {
        if (effectiveExecutionStatus === "running" || isLoadingDeferredOutput) {
            body = (
                <ContentPlaceholder
                    definitionId={definitionId}
                    status="running"
                    mode={mode}
                    instanceKind={instanceKind}
                    onRun={handleRun}
                />
            );
        } else if (effectiveExecutionStatus === "error" || deferredOutputError) {
            body = (
                <ContentPlaceholder
                    definitionId={definitionId}
                    status="error"
                    mode={mode}
                    instanceKind={instanceKind}
                    errorMessage={deferredOutputError ?? effectiveExecutionError ?? t("common.unknown")}
                    onRun={handleRun}
                />
            );
        } else if (isMemberRenderWaitingForRootOutput) {
            body = (
                <ContentPlaceholder
                    definitionId={definitionId}
                    status="idle"
                    mode={mode}
                    instanceKind={instanceKind}
                    onRun={handleRun}
                />
            );
        } else if (contentOutput) {
            body = (
                <ScopeProvider
                    definitions={scopedUnits.definitions}
                    forDefinitions={scopedUnits.forDefinitions}
                    contentDefinitions={scopedUnits.contentDefinitions}
                    unitOutputs={{
                        ...contentOutputToScopeUnitOutputs(contentOutput),
                        ...scopedUnits.unitOutputs,
                    }}
                >
                    {renderCurrentBody()}
                </ScopeProvider>
            );
        } else if (hasPersistedInnerOutputs) {
            body = (
                <ScopeProvider
                    definitions={scopedUnits.definitions}
                    forDefinitions={scopedUnits.forDefinitions}
                    contentDefinitions={scopedUnits.contentDefinitions}
                    unitOutputs={persistedInnerOutputs}
                >
                    {renderCurrentBody()}
                </ScopeProvider>
            );
        } else if (hasScopedUnits) {
            body = (
                <ScopeProvider
                    definitions={scopedUnits.definitions}
                    forDefinitions={scopedUnits.forDefinitions}
                    contentDefinitions={scopedUnits.contentDefinitions}
                    unitOutputs={scopedUnits.unitOutputs}
                >
                    {renderCurrentBody()}
                </ScopeProvider>
            );
        } else {
            body = (
                <ContentPlaceholder
                    definitionId={definitionId}
                    status="idle"
                    mode={mode}
                    instanceKind={instanceKind}
                    onRun={handleRun}
                />
            );
        }
    }

    const footerLabel = getContentRenderMeta(t, instanceKind).label;

    const isPlaceholderBody = needsExec && (
        effectiveExecutionStatus === "running" ||
        isLoadingDeferredOutput ||
        effectiveExecutionStatus === "error" ||
        !!deferredOutputError ||
        isMemberRenderWaitingForRootOutput ||
        (!contentOutput && !hasPersistedInnerOutputs && !hasScopedUnits)
    );
    const showFooter = !!(doc.executable && !isTemplateContent && !isPlaceholderBody);

    if (!expanded) {
        const collapsedStatus: CollapsedUnitRenderStatus =
            isImportRunning
                ? "running"
                : effectiveExecutionStatus === "error" || deferredOutputError
                    ? "error"
                    : effectiveExecutionStatus === "running" || isLoadingDeferredOutput
                        ? "running"
                        : execution?.status === "success" ||
                            !!contentOutput ||
                            hasPersistedInnerOutputs
                            ? "success"
                            : null;
        return (
            <CollapsedUnitRenderHeader
                unitId={definitionId}
                label={footerLabel}
                technicalLabel={instanceKind}
                status={collapsedStatus}
                statusLabels={{
                    success: needsExec ? t("render.status.ran") : t("render.status.rendered"),
                    error: deferredOutputError ?? effectiveExecutionError ?? t("render.status.failed"),
                }}
                onExpand={() => setExpanded(true)}
            />
        );
    }

    return (
        <div className="relative group/content-block">
            <div
                ref={renderContainerRef}
                className={cn(
                    "relative magic-render-unit group/content-render",
                    isFullscreen
                        ? "fixed z-[9000] m-0 flex flex-col overflow-hidden bg-preview-surface p-4 text-foreground"
                        : undefined,
                )}
                style={fullscreenContainerStyle}
                data-render-fullscreen={isFullscreen ? "true" : undefined}
                data-render-fullscreen-scope={isFullscreen ? "workspace" : undefined}
            >
                <div
                    ref={renderBodyRef}
                    className={cn(
                        "magic-render-body",
                        isFullscreen ? "magic-render-fullscreen-content" : undefined,
                    )}
                    data-render-fullscreen-width-preset={isFullscreen ? fullscreenWidthPreset : undefined}
                    data-md-rendered-output-hash={outputHash}
                    style={renderBodyStyle}
                >
                    {isFullscreen && (
                        <>
                            <button
                                type="button"
                                role="separator"
                                aria-orientation="vertical"
                                aria-label={t("render.fullscreen.resizeLeft")}
                                title={t("render.fullscreen.resizeTitle")}
                                className="group/resize absolute inset-y-0 left-0 z-20 w-2 -translate-x-3 cursor-col-resize border-0 bg-transparent p-0 outline-none"
                                onMouseDown={(event) => startFullscreenWidthResize("left", event)}
                                onDoubleClick={resetFullscreenWidth}
                            >
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors",
                                        isFullscreenWidthDragging
                                            ? "bg-accent"
                                            : "bg-transparent group-hover/resize:bg-accent group-focus-visible/resize:bg-accent",
                                    )}
                                />
                            </button>
                            <button
                                type="button"
                                role="separator"
                                aria-orientation="vertical"
                                aria-label={t("render.fullscreen.resizeRight")}
                                title={t("render.fullscreen.resizeTitle")}
                                className="group/resize absolute inset-y-0 right-0 z-20 w-2 translate-x-3 cursor-col-resize border-0 bg-transparent p-0 outline-none"
                                onMouseDown={(event) => startFullscreenWidthResize("right", event)}
                                onDoubleClick={resetFullscreenWidth}
                            >
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors",
                                        isFullscreenWidthDragging
                                            ? "bg-accent"
                                            : "bg-transparent group-hover/resize:bg-accent group-focus-visible/resize:bg-accent",
                                    )}
                                />
                            </button>
                        </>
                    )}
                    {isFullscreen ? (
                        <div className="magic-render-fullscreen-content-inner">
                            {body}
                        </div>
                    ) : (
                        body
                    )}
                </div>
                {showFooter && (
                    <RenderUnitFooterDisclosure
                        autoOpen={
                            effectiveExecutionStatus === "running" ||
                            effectiveExecutionStatus === "error" ||
                            isLoadingDeferredOutput ||
                            !!deferredOutputError ||
                            isImportRunning ||
                            justFinished
                        }
                        footerClassName={cn(
                            "magic-render-footer flex h-7 min-w-0 items-center gap-2 px-2 text-[10px] text-muted-foreground",
                            isFullscreen
                                ? "sticky bottom-0 z-20 -mx-4 mt-2 shrink-0 border-t border-border bg-preview-surface/95 backdrop-blur"
                                : undefined,
                        )}
                        isFullscreen={isFullscreen}
                        toggleLabel={definitionId}
                    >
                        <div className="magic-render-footer-leading flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                            {effectiveExecutionStatus === "running" || isLoadingDeferredOutput || isImportRunning ? (
                                <>
                                    <span
                                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border-[1.5px] border-t-transparent animate-spin"
                                        style={{
                                            borderColor: getThemeAccentValue("running"),
                                            borderTopColor: "transparent",
                                        }}
                                    />
                                    <span
                                        className="magic-render-footer-message theme-accent-text min-w-0 max-w-[200px] truncate whitespace-nowrap"
                                        style={getThemeAccentStyle("running")}
                                    >
                                        {isImportRunning
                                            ? t("render.importRunning", {
                                                namespace: importNamespace ?? "",
                                            })
                                            : t("render.content.parsingEllipsis")}
                                    </span>
                                </>
                            ) : effectiveExecutionStatus === "error" || deferredOutputError ? (
                                <span
                                    className="magic-render-footer-message theme-accent-text min-w-0 max-w-[200px] truncate whitespace-nowrap"
                                    style={getThemeAccentStyle("error")}
                                >
                                    {deferredOutputError
                                        ? t("render.placeholder.outputLoadFailedShort")
                                        : t("render.status.failed")}
                                </span>
                            ) : execution?.status === "success" || contentOutput || Object.keys(persistedInnerOutputs).length > 0 ? (
                                <span
                                    className="magic-render-footer-status inline-flex shrink-0 items-center gap-1 whitespace-nowrap theme-accent-text"
                                    style={getThemeAccentStyle("success")}
                                >
                                    <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    <span>
                                        {needsExec
                                            ? t("render.status.ran")
                                            : t("render.status.rendered")}
                                    </span>
                                </span>
                            ) : (
                                <span className="magic-render-footer-status shrink-0 whitespace-nowrap text-muted-foreground/70">
                                    {t("render.status.notRun")}
                                </span>
                            )}
                            {effectiveExecutionDuration != null && effectiveExecutionStatus !== "running" && !isLoadingDeferredOutput && !isImportRunning && (
                                <span className="magic-render-footer-secondary shrink-0 whitespace-nowrap text-muted-foreground/60">{effectiveExecutionDuration}ms</span>
                            )}
                            <span className="magic-render-footer-plugin min-w-0 max-w-[160px] truncate whitespace-nowrap text-muted-foreground/40 font-mono">
                                {definitionId} · {footerLabel}
                            </span>
                        </div>
                        <div
                            className="magic-render-footer-actions ml-auto flex shrink-0 items-center gap-1"
                            data-plugin-render-footer-actions="true"
                        >
                        <span className="contents magic-render-footer-action-secondary magic-render-footer-action-annotations">
                            <AnnotationBlockFooterControl
                                blockId={`render:${definitionId}`}
                                label={definitionId}
                                outputHash={outputHash}
                            />
                        </span>
                        <UnitFooterCopyButton
                            primaryText={copyReferenceText}
                            actions={copyActions}
                            className="magic-render-footer-action-secondary magic-render-footer-action-copy"
                        />
                        {createElement(UnitDependencyFooterButton, { targetId: definitionId })}
                        {canRunImport && (
                            <button
                                type="button"
                                className={cn(
                                    "magic-render-footer-action-secondary magic-render-footer-action-import flex h-5 w-5 items-center justify-center rounded transition-colors duration-150",
                                    isImportRunning
                                        ? "cursor-wait text-muted-foreground/50"
                                        : "cursor-pointer text-muted-foreground hover:bg-muted hover:text-foreground",
                                )}
                                onClick={handleRunImport}
                                disabled={isImportRunning}
                                title={isImportRunning
                                    ? t("render.importRunning", {
                                        namespace: importNamespace ?? "",
                                    })
                                    : t("render.rerunImport", {
                                        namespace: importNamespace ?? "",
                                    })}
                                aria-label={isImportRunning
                                    ? t("render.importRunning", {
                                        namespace: importNamespace ?? "",
                                    })
                                    : t("render.rerunImport", {
                                        namespace: importNamespace ?? "",
                                    })}
                            >
                                {isImportRunning ? (
                                    <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                                ) : (
                                    <RefreshCw size={11} aria-hidden="true" />
                                )}
                            </button>
                        )}
                        {isFullscreen && (
                            <div className="magic-render-footer-action-secondary magic-render-footer-action-fullscreen-width relative">
                                <button
                                    type="button"
                                    className="flex items-center justify-center h-5 min-w-5 rounded px-1 text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted transition-colors"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setIsFullscreenWidthMenuOpen((open) => !open);
                                    }}
                                    title={t("render.fullscreen.adjustWidthTitle", {
                                        label: currentFullscreenWidthLabel,
                                    })}
                                    aria-label={t("render.fullscreen.adjustWidth")}
                                    aria-haspopup="menu"
                                    aria-expanded={isFullscreenWidthMenuOpen}
                                >
                                    <StretchHorizontal size={11} />
                                </button>
                                {isFullscreenWidthMenuOpen && (
                                    <div
                                        role="menu"
                                        className="absolute right-0 bottom-6 z-30 w-28 overflow-hidden rounded-md border border-border bg-background py-1 text-[11px] shadow-lg"
                                    >
                                        {PREVIEW_WIDTH_PRESETS.map((preset) => (
                                            <button
                                                key={preset.id}
                                                type="button"
                                                role="menuitemradio"
                                                aria-checked={fullscreenWidthPreset === preset.id}
                                                className={cn(
                                                    "flex w-full items-center justify-between px-2 py-1 text-left transition-colors hover:bg-muted",
                                                    fullscreenWidthPreset === preset.id
                                                        ? "text-foreground"
                                                        : "text-muted-foreground",
                                                )}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleFullscreenWidthPresetSelect(preset.id);
                                                }}
                                            >
                                                <span>{t(`previewWidth.${preset.id}`)}</span>
                                                <span className="font-mono text-[10px] text-muted-foreground/60">
                                                    {preset.width === "fill" ? "100%" : preset.width}
                                                </span>
                                            </button>
                                        ))}
                                        {fullscreenWidthPreset === "custom" && customFullscreenWidth && (
                                            <div className="border-t border-border px-2 py-1 text-muted-foreground">
                                                {t("previewWidth.customWithWidth", {
                                                    width: Math.round(customFullscreenWidth),
                                                })}
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            role="menuitem"
                                            className="flex w-full items-center px-2 py-1 text-left text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                resetFullscreenWidth();
                                            }}
                                        >
                                            {t("previewWidth.reset")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            type="button"
                            className="magic-render-footer-action-secondary magic-render-footer-action-fullscreen flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted transition-colors"
                            onClick={handleToggleFullscreen}
                            title={isFullscreen ? t("render.fullscreen.exit") : t("render.fullscreen.enter")}
                            aria-label={isFullscreen ? t("render.fullscreen.exit") : t("render.fullscreen.enter")}
                        >
                            {isFullscreen ? <Minimize2 size={11} /> : <Maximize2 size={11} />}
                        </button>
                        {onRun && needsExec && (
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleRun();
                                }}
                                disabled={effectiveExecutionStatus === "running" || isLoadingDeferredOutput}
                                className={cn(
                                    "magic-render-footer-action-primary magic-render-footer-action-run flex items-center justify-center h-5 w-5 rounded transition-colors duration-150",
                                    effectiveExecutionStatus === "running" || isLoadingDeferredOutput
                                        ? "text-muted-foreground/50 cursor-wait"
                                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                                )}
                                title={t("render.action.run")}
                                aria-label={t("render.action.run")}
                            >
                                <Play size={12} fill="currentColor" />
                            </button>
                        )}
                        </div>
                    </RenderUnitFooterDisclosure>
                )}
            </div>
        </div>
    );
});

/* ── ContentPlaceholder ── */

function ContentPlaceholder({
    definitionId,
    status,
    mode,
    instanceKind = "content",
    errorMessage,
    onRun,
}: {
    definitionId: string;
    status: "idle" | "running" | "error";
    mode: RenderMode;
    instanceKind?: "content" | "use-content" | "scoped-content";
    errorMessage?: string;
    onRun?: () => void;
}) {
    const t = useT("editor");
    const isRunning = status === "running";
    const hasError = status === "error";
    const meta = getContentRenderMeta(t, instanceKind);
    const accentStyle = isRunning
        ? getThemeAccentStyle("running")
        : hasError
            ? getThemeAccentStyle("error")
            : undefined;
    const statusLabel = isRunning
        ? t("render.content.parsing")
        : hasError
            ? t("render.status.failed")
            : t("render.status.notRun");
    const message = isRunning
        ? t("render.content.parsingMessage")
        : hasError
            ? (errorMessage ?? t("render.content.failedMessage"))
            : t("render.content.notRunMessage");

    if (mode === "inline") {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                {isRunning ? (
                    <>
                        <Loader2 size={10} className="animate-spin" />
                        {t("render.content.parsingEllipsis")}
                    </>
                ) : hasError ? (
                    <>
                        <AlertTriangle
                            size={10}
                            className="theme-accent-text"
                            style={getThemeAccentStyle("error")}
                        />
                        <span
                            className="theme-accent-text opacity-80"
                            style={getThemeAccentStyle("error")}
                            title={message}
                        >
                            {definitionId}
                        </span>
                    </>
                ) : (
                    <>
                        <FileText size={10} />
                        <span className="opacity-60">{definitionId}</span>
                        {onRun && (
                            <button
                                type="button"
                                onClick={onRun}
                                className="ml-0.5 hover:text-foreground transition-colors"
                            >
                                <Play size={9} />
                            </button>
                        )}
                    </>
                )}
            </span>
        );
    }

    return (
        <div
            className={cn(
                "my-1.5 rounded-lg border border-dashed bg-background px-3 py-2.5",
                "transition-colors",
                isRunning || hasError
                    ? "theme-accent-banner"
                    : "border-border",
            )}
            style={accentStyle}
            data-content-render-empty="true"
        >
            <div className="flex min-w-0 items-center gap-3">
                <RenderUnitBadge {...meta.badge} />

                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-mono text-[13px] font-medium text-foreground">
                            {definitionId}
                        </span>
                        <span
                            className={cn(
                                "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium",
                                isRunning || hasError
                                    ? "theme-accent-chip"
                                    : "bg-muted text-muted-foreground",
                            )}
                            style={accentStyle}
                        >
                            {statusLabel}
                        </span>
                    </div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span className="shrink-0">{meta.label}</span>
                        <span className="text-muted-foreground/40">·</span>
                        <span
                            className="hidden min-w-0 truncate font-mono text-muted-foreground/50 sm:inline"
                            title={meta.technicalLabel}
                        >
                            {meta.technicalLabel}
                        </span>
                    </div>
                    <div
                        className={cn(
                            "mt-1 truncate text-[11px]",
                            hasError
                                ? "theme-accent-text"
                                : "text-muted-foreground/70",
                        )}
                        style={hasError ? getThemeAccentStyle("error") : undefined}
                        title={message}
                    >
                        {message}
                    </div>
                </div>

                {hasError && (
                    <AlertTriangle
                        size={15}
                        className="shrink-0 theme-accent-text"
                        style={getThemeAccentStyle("error")}
                        aria-label={t("render.status.failed")}
                    />
                )}

                {onRun && (
                    <button
                        type="button"
                        data-magic-md-input-confirm="true"
                        className={cn(
                            "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 text-[11px] font-medium",
                            "transition-colors",
                            isRunning
                                ? "cursor-wait bg-muted text-muted-foreground"
                                : "bg-primary text-primary-foreground hover:bg-primary/90",
                        )}
                        onClick={onRun}
                        disabled={isRunning}
                        aria-label={t("render.runUnit", { id: definitionId })}
                    >
                        {isRunning ? (
                            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                        ) : (
                            <Play size={13} aria-hidden="true" />
                        )}
                        <span>{t("render.action.run")}</span>
                    </button>
                )}
            </div>
        </div>
    );
}
